import React, { useEffect, useMemo, useRef, useState } from "react";

function roleLabel(role) {
  if (!role) return "";
  return role.name || String(role.id);
}

function matchesQuery(role, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    roleLabel(role).toLowerCase().includes(q) ||
    String(role.id).includes(q)
  );
}

/**
 * Searchable role picker - same UI pattern as ChannelPicker.
 */
export default function RolePicker({
  roles = [],
  value,
  onChange,
  multiple = false,
  placeholder,
  emptyLabel = "None",
  allowEmpty = true,
  disabled = false,
  excludeEveryone = true,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const options = useMemo(() => {
    const list = (Array.isArray(roles) ? roles : []).filter((role) => {
      if (!role) return false;
      if (excludeEveryone && role.name === "@everyone") {
        return false;
      }
      return matchesQuery(role, query);
    });
    return list;
  }, [roles, query, excludeEveryone]);

  const selectedIds = multiple
    ? Array.isArray(value)
      ? value.map(String)
      : []
    : value
      ? [String(value)]
      : [];

  const selectedRole = !multiple
    ? roles.find((r) => String(r.id) === String(value || ""))
    : null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const displayPlaceholder = placeholder || "Select a role...";

  const showTriggerText = multiple
    ? selectedIds.length
      ? `${selectedIds.length} selected`
      : displayPlaceholder
    : selectedRole
      ? roleLabel(selectedRole)
      : displayPlaceholder;

  const notifyDirty = () => {
    rootRef.current?.dispatchEvent(
      new CustomEvent("settings-dirty", { bubbles: true }),
    );
  };

  const pick = (id) => {
    if (multiple) {
      const next = selectedIds.includes(String(id))
        ? selectedIds.filter((x) => x !== String(id))
        : [...selectedIds, String(id)];
      onChange(next);
      setQuery("");
      inputRef.current?.focus();
      notifyDirty();
      return;
    }
    onChange(id);
    setOpen(false);
    setQuery("");
    notifyDirty();
  };

  const clear = (e) => {
    e.stopPropagation();
    if (multiple) onChange([]);
    else onChange("");
    setQuery("");
    notifyDirty();
  };

  const removeChip = (id) => {
    if (!multiple) return;
    onChange(selectedIds.filter((x) => x !== String(id)));
    notifyDirty();
  };

  return (
    <div
      className={`channel-picker ${disabled ? "is-disabled" : ""} ${className}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`channel-picker-trigger settings-input ${open ? "is-open" : ""}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        <span
          className={`channel-picker-value ${
            !selectedRole && !selectedIds.length ? "is-placeholder" : ""
          }`}
        >
          {showTriggerText}
        </span>
        <span className="channel-picker-actions">
          {allowEmpty &&
            (multiple ? selectedIds.length > 0 : Boolean(value)) && (
              <span
                className="channel-picker-clear"
                role="button"
                tabIndex={-1}
                onClick={clear}
                aria-label="Clear selection"
              >
                ×
              </span>
            )}
          <span className="channel-picker-caret" aria-hidden />
        </span>
      </button>

      {open && (
        <div className="channel-picker-menu" role="listbox">
          <input
            ref={inputRef}
            type="text"
            className="channel-picker-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type to filter roles…"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="channel-picker-options">
            {allowEmpty && !multiple && (
              <button
                type="button"
                className={`channel-picker-option ${!value ? "is-selected" : ""}`}
                onClick={() => pick("")}
              >
                {emptyLabel}
              </button>
            )}
            {options.length === 0 && (
              <div className="channel-picker-empty">No matches</div>
            )}
            {options.map((role) => {
              const selected = selectedIds.includes(String(role.id));
              return (
                <button
                  type="button"
                  key={role.id}
                  className={`channel-picker-option ${selected ? "is-selected" : ""}`}
                  onClick={() => pick(role.id)}
                >
                  {multiple && (
                    <span className="channel-picker-check" aria-hidden>
                      {selected ? "✓" : ""}
                    </span>
                  )}
                  <span>{roleLabel(role)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {multiple && selectedIds.length > 0 && (
        <div className="channel-picker-chips">
          {selectedIds.map((id) => {
            const role = roles.find((r) => String(r.id) === String(id));
            return (
              <span key={id} className="channel-picker-chip">
                {role ? roleLabel(role) : id}
                <button
                  type="button"
                  className="channel-picker-chip-remove"
                  onClick={() => removeChip(id)}
                  aria-label={`Remove ${role?.name || id}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
